import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Search, Loader2, BadgeCheck, X } from "lucide-react";

/**
 * UserSearch — reusable inline user finder.
 * Props:
 *  - onPick(user): callback when a user is selected (optional)
 *  - placeholder: input placeholder
 *  - excludeUserIds: array of user IDs to filter out (e.g., self)
 *  - autoFocus
 *  - pickLabel: label for the pick button (default "Select")
 *  - linkMode: if true (default when no onPick provided), tapping a result navigates to /u/:id
 */
export default function UserSearch({ onPick, placeholder = "Search by name, @username or email...", excludeUserIds = [], autoFocus = false, pickLabel = "Select", linkMode }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const t = q.trim();
    if (!t) { setResults([]); setLoading(false); return; }
    setLoading(true);
    timer.current = setTimeout(() => {
      api.get("/users/search", { params: { q: t, limit: 25 } })
        .then(r => {
          const filtered = (r.data || []).filter(u => !excludeUserIds.includes(u.id));
          setResults(filtered);
        })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 280);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [q]);

  const useLink = linkMode === true || (linkMode !== false && !onPick);

  return (
    <div className="space-y-2" data-testid="user-search">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40"/>
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          autoFocus={autoFocus}
          placeholder={placeholder}
          data-testid="user-search-input"
          className="w-full pl-9 pr-9 py-2.5 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#00E5FF] text-sm"
        />
        {q && (
          <button onClick={() => setQ("")} data-testid="user-search-clear"
            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
            <X className="w-3.5 h-3.5"/>
          </button>
        )}
      </div>

      <div className="space-y-1.5 max-h-96 overflow-y-auto scrollbar-hide">
        {loading && (
          <div className="flex items-center gap-2 text-xs text-white/50 py-2 px-1">
            <Loader2 className="w-3 h-3 animate-spin"/> Searching...
          </div>
        )}
        {!loading && q && results.length === 0 && (
          <p className="text-center text-white/40 text-sm py-4" data-testid="user-search-empty">No users match "{q}"</p>
        )}
        {results.map(u => {
          const body = (
            <>
              <img src={u.avatar} alt="" className="w-10 h-10 rounded-full object-cover"/>
              <div className="flex-1 min-w-0 text-left">
                <div className="flex items-center gap-1">
                  <span className="font-bold text-sm truncate">{u.name}</span>
                  {u.verified && <BadgeCheck className="w-3.5 h-3.5 text-[#00E5FF] flex-shrink-0"/>}
                </div>
                <div className="text-[11px] text-white/60 truncate">@{u.username} • {(u.followers || 0).toLocaleString()} followers</div>
              </div>
            </>
          );
          if (useLink) {
            return (
              <Link to={`/u/${u.id}`} key={u.id} data-testid={`user-result-${u.id}`}
                className="flex items-center gap-3 p-2.5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 active:scale-[0.98] transition">
                {body}
              </Link>
            );
          }
          return (
            <button key={u.id} type="button" onClick={() => onPick(u)} data-testid={`user-result-${u.id}`}
              className="w-full flex items-center gap-3 p-2.5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 active:scale-[0.98] transition">
              {body}
              <span className="text-[11px] font-bold text-[#00E5FF] flex-shrink-0">{pickLabel}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
